import { IPSeparator, IPText } from 'baseComponents';
import { IPDefaultButton, IPPrimaryButton } from 'baseComponents/Button/IPButton';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface IActionCalloutProps {
  // the DOM selector of the element the callout attaches to, e.g. '#my-command-bar-button'
  target: string;
  title: string;
  isSaving?: boolean;
  saveText?: string;
  saveDisabled?: boolean;
  width?: number;
  setInitialFocus?: boolean;
  hideFooterSeparator?: boolean;
  onSave: () => void;
  onDismiss: () => void;
  children: ReactNode;
}

const defaultWidth = 320;

// The house format for a callout that edits something and confirms it: bold title, spaced body, a
// separator and a right-aligned Save/Cancel pair. Anything that asks the user for a value on the
// spot uses this instead of building the frame again.
export const ActionCallout = (props: IActionCalloutProps) => {
  const { t } = useTranslation(['translation']);

  return (
    <IPCallout
      styles={{ root: { padding: 20, maxWidth: props.width ?? defaultWidth } }}
      setInitialFocus={props.setInitialFocus !== false}
      gapSpace={0}
      target={props.target}
      onDismiss={props.onDismiss}
    >
      <IPStack tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText variant="mediumPlus" styles={globalTextStylesBold}>
            {props.title}
          </IPText>
        </IPStack.Item>
        <IPStack.Item>{props.children}</IPStack.Item>
        {!props.hideFooterSeparator && <IPSeparator />}
        <IPStack.Item>
          <IPStack horizontal horizontalAlign="end" tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPPrimaryButton
                text={props.saveText ?? t('translation:General.Button.Save')}
                disabled={props.saveDisabled === true || props.isSaving === true}
                onClick={props.onSave}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPDefaultButton text={t('translation:General.Button.Cancel')} onClick={props.onDismiss} />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </IPCallout>
  );
};
